import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { HttpService } from './http.service';
import { BehaviorSubject, Observable, of, retry } from 'rxjs';
import { catchError, filter, map } from 'rxjs/operators';
import { NOT_CANCELLED_HEADER } from '../interceptors/auth.interceptor';

export interface IStore {
	id: number;
	name: string;
	markers?: Array<number>;
	group?: number;
}

export interface IStoreUseInfo {
	id: number;
	name: string;
	used: boolean;
}

@Injectable({
	providedIn: 'root'
})
export class StoresService {
	private _getShopsSubject: BehaviorSubject<Array<IStore>> = new BehaviorSubject<Array<IStore>>(null);
	private _loading: boolean = false;

	get getShops$(): Observable<Array<IStore>> {
		if (!this._getShopsSubject.value && !this._loading) {
			this._get()
				.pipe(
					retry({
						count: 5,
						delay: 1000
					}),
					catchError(() => of([]))
				)
				.subscribe((response: Array<IStore>) => {
					this._getShopsSubject.next(response);
				});
		}
		this._loading = true;
		return this._getShopsSubject.pipe(
			filter((value: Array<IStore>) => value !== null)
		);
	}

	constructor(
		private http: HttpClient,
		private httpService: HttpService
	) {}

	private _transformMarkerData(data: Array<any>): Array<any> {
		return data.map(item => ({
			...item,
			name: item.title || item.name,
			parent_id: item.pId
		}));
	}

	private _createTree(data: Array<any>, id: number): Array<any> {
		const markers = data.filter(m => m.parent_id == id) || [];
		markers.forEach(m => {
			m.children = this._createTree(data, m.id);
			m.has_children = m.isLeaf === false || !!m.children.length;
			m.loading = false;
		});
		return markers;
	}

	private _get(): Observable<Array<IStore>> {
		return this.http.get<Array<IStore>>(
            '/api/data/shops/?fields=id,name,group,markers', 
            { headers: new HttpHeaders().set(NOT_CANCELLED_HEADER, 'true') }
        );
	}

	getShopGroups(): Promise<Array<any>> {
		return this.httpService.getPromise<Array<any>>(
			this.http.get<Array<any>>('/api/data/shop-groups/get_names/')
		);
	}

	getShopMarkers(): Promise<Array<any>> {
		const queryParams: HttpParams = new HttpParams().append('level', 2);

		return this.httpService.getPromise<Array<any>>(
			this.http.get<{count: number, results: Array<any>}>('/api/filters/shop-markers-tree/', { params: queryParams }).pipe(
				map((response: {count: number, results: Array<any>}) => {
					const transformedData = this._transformMarkerData(response.results);
					return this._createTree(transformedData, 1);
				})
			)
		)
	}

	getShopMarkerChildren(id: number): Promise<Array<any>> {
		const queryParams: HttpParams = new HttpParams();

		return this.httpService.getPromise<Array<any>>(
			this.http.get<{count: number, results: Array<any>}>(`/api/filters/shop-markers-tree/${id}/`, { params: queryParams }).pipe(
				map((response: {count: number, results: Array<any>}) => {
					const transformedData = this._transformMarkerData(response.results);
					return this._createTree(transformedData, id);
				})
			)
		)
	}

	getShopsUseInfo(): Observable<Array<IStoreUseInfo>> {
		return this.http.get<Array<IStoreUseInfo>>('/api/data/shops/?fields=id,name,used');
	}

	getUnusedShops(): Observable<Array<IStore>> {
		return this.http.get<Array<IStore>>('/api/data/shops/?fields=id,name&used=false');
	}

	formatChildren(list: Array<any>, parent: any): Array<any> {
		return list.map(item => {
			item.id = item.id || Number(item.group_id || item.format_id);
			item.name = item.name || item.format_name;
			item.has_children = item.isLeaf === false || !!(item.children && item.children.length);
			item.level = parent.level + 1;
			item.parent = parent;

			if (item.has_children && item.children && item.children.length) {
				item.children = this.formatChildren(item.children, item);
			}

			return item;
		});
	}
}
